import { useSDK } from "@metamask/sdk-react";
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSelector } from "react-redux";
import Web3 from "web3";
import { message } from "antd";
import _ from "lodash";

export const MetaMaskContext = createContext();

export default function MetaMaskSDKContainer({ children }) {
  const { sdk, provider } = useSDK();

  const [chain, setChain] = useState("");
  const [account, setAccount] = useState();
  const [response, setResponse] = useState("");
  const [serviceStatus, setServiceStatus] = useState();

  const { IS_TESTNET } = useSelector((state) => state.app.config);
  const requiredChainId = useMemo(
    () => (IS_TESTNET ? "0xAA36A7" : Web3.utils.toHex("1")),
    [IS_TESTNET]
  );

  const connect = useCallback(async () => {
    if (!provider) {
      throw new Error(`invalid ethereum provider`);
    }

    try {
      const accounts = await sdk?.connect();
      if (!accounts || accounts.length === 0) {
        return null;
      }
      console.debug(`connect:: accounts result`, accounts);

      setAccount(accounts[0]);
      return accounts[0];
    } catch (error) {
      console.log("request accounts ERR", error);
    }
  }, [sdk, provider]);

  const requestSwitchChain = useCallback(async () => {
    if (_.isUndefined(IS_TESTNET)) {
      return;
    }

    // switch chain
    try {
      await provider?.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: requiredChainId }],
      });
      return true;
    } catch (error) {
      // This error code indicates that the chain has not been added to MetaMask.
      if (error.code === 4902) {
        try {
          const resp = await provider?.request({
            method: "wallet_addEthereumChain",
            params: [
              {
                chainId: requiredChainId,
                chainName: IS_TESTNET ? "Sepolia" : "Ethereum Mainnet",
                rpcUrls: IS_TESTNET
                  ? ["https://sepolia.drpc.org"]
                  : ["https://eth.drpc.org"],
              },
            ],
          });
          const currentChainId = await provider?.request({
            method: "eth_chainId",
          });

          // Check if user cancelled the switch network after added
          if (currentChainId !== requiredChainId) {
            message.error("Please switch to requested network.");
            return false;
          }

          return true;
        } catch (err) {
          console.log(err);
          if (err.code === 4001) {
            message.error("Please add the requested network.");
          }
        }
      }
      // User rejected switch network
      if (error.code === 4001) {
        message.error("Please switch to requested network.");
        localStorage.removeItem("wallet_address");
        if (
          window.location.pathname === "/" ||
          window.location.pathname === "/tnc" ||
          window.location.pathname === "/faq"
        ) {
          return false;
        }
        window.location.href = "/";
        return false;
      }

      // message.error(error.message);
      return false;
    }
  }, [IS_TESTNET, requiredChainId, provider]);

  useEffect(() => {
    if (!sdk || !provider) {
      return;
    }

    // activeProvider is mapped to window.ethereum.
    console.debug(`App::useEffect setup active provider listeners`);
    if (provider?.selectedAddress) {
      console.debug(`App::useEffect setting account from window.ethereum `);
      setAccount(provider?.selectedAddress);
    }

    if (provider?.chainId) {
      console.debug(`App::useEffect setting chainId from window.ethereum `);
      setChain(provider.chainId);
      const wallet_address = localStorage.getItem("wallet_address");
      if (wallet_address !== null && chain && chain !== requiredChainId) {
        console.log("chain_not_match");
        requestSwitchChain();
      }
    }

    const onChainChanged = (newChain) => {
      console.log(`App::useEffect on 'chainChanged'`, newChain);

      const wallet_address = localStorage.getItem("wallet_address");
      if (wallet_address !== null && newChain !== requiredChainId) {
        localStorage.removeItem("wallet_address");
        if (
          window.location.pathname === "/" ||
          window.location.pathname === "/tnc" ||
          window.location.pathname === "/faq"
        ) {
          return;
        }
        window.location.href = "/";
      }

      setChain(newChain);
    };

    const onInitialized = () => {
      console.debug(`App::useEffect on _initialized`);
      if (provider?.selectedAddress) {
        setAccount(provider?.selectedAddress);
      }

      if (provider?.chainId) {
        setChain(provider.chainId);
      }
    };

    const onAccountsChanged = (accounts) => {
      console.log(`App::useEffect on 'accountsChanged'`, accounts);
      console.log("changed ac");

      const wallet_address = localStorage.getItem("wallet_address");
      if (wallet_address !== null) {
        localStorage.removeItem("wallet_address");
        if (
          window.location.pathname === "/" ||
          window.location.pathname === "/tnc" ||
          window.location.pathname === "/faq"
        ) {
          return;
        }
        window.location.href = "/";
      }

      setAccount(accounts?.[0]);
    };

    const onConnect = (_connectInfo) => {
      console.log(`App::useEffect on 'connect'`, _connectInfo);

      if (provider?.chainId) {
        setChain(provider.chainId);
      }
    };

    const onDisconnect = (error) => {
      console.log(`App::useEffect on 'disconnect'`, error);
      setChain("");
    };

    const onServiceStatus = (_serviceStatus) => {
      console.debug(`sdk connection_status`, _serviceStatus);
      setServiceStatus(_serviceStatus);
    };

    provider?.on("chainChanged", onChainChanged);

    provider?.on("_initialized", onInitialized);

    provider?.on("accountsChanged", onAccountsChanged);

    provider?.on("connect", onConnect);

    provider?.on("disconnect", onDisconnect);

    sdk.on('service_status', onServiceStatus);

    return () => {
      console.debug(`App::useEffect cleanup activeprovider events`);
      provider?.removeListener("chainChanged", onChainChanged);
      provider?.removeListener("_initialized", onInitialized);
      provider?.removeListener("accountsChanged", onAccountsChanged);
      provider?.removeListener("connect", onConnect);
      provider?.removeListener("disconnect", onDisconnect);
      sdk.removeListener('service_status', onServiceStatus);
    };
  }, [
    sdk,
    provider,
    chain,
    IS_TESTNET,
    requiredChainId,
    requestSwitchChain,
  ]);

  useEffect(() => {
    if (!sdk?.isInitialized()) {
      return;
    }

    const onProviderEvent = (accounts) => {
      if (accounts?.[0]?.startsWith("0x")) {
        setAccount(accounts?.[0]);
      } else {
        setAccount(undefined);
      }
    };
    // listen for provider change events
    sdk.on('provider_update', onProviderEvent);
    return () => {
      sdk.removeListener('provider_update', onProviderEvent);
    };
  }, [sdk]);

  const terminate = useCallback(() => {
    if (!sdk) {
      return;
    }
    sdk.terminate();
  }, [sdk]);

  const context = useMemo(
    () => ({
      sdk,
      chain,
      account,
      response,
      serviceStatus,
      connect,
      terminate,
      requestSwitchChain,
    }),
    [
      sdk,
      chain,
      account,
      response,
      serviceStatus,
      connect,
      terminate,
      requestSwitchChain,
    ]
  );

  return (
    <MetaMaskContext.Provider value={context}>
      {children}
    </MetaMaskContext.Provider>
  );
}
